import React, { useMemo } from 'react'
import { useFeatureFlags } from '@node-space/hooks'
import { Avatar, ComboboxItem } from '@node-space/ui-components'
import { useGetCustomersWalletProfileQuery } from 'hooks/queries/Customers/useCustomerWalletProfilesQuery'
import { CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS } from 'pages/Customers/constants'
import { BeneficiaryDetailsCurrency } from 'types/beneficiaries'
import { stringArrayToFriendlyText } from 'utils/utils'
import { WalletProfileMapping } from '../types'
import { getOptions } from '../utils'

export const useFormOptions = (currency: string, isEmbeddedCustomer: boolean) => {
  const { enableAssignCustomerWallet } = useFeatureFlags()

  const {
    data: walletProfiles,
    isFetching: isFetchingWalletProfiles,
    error: walletProfilesError,
  } = useGetCustomersWalletProfileQuery(enableAssignCustomerWallet)

  const profileMappings: WalletProfileMapping = useMemo(() => {
    return walletProfiles?.profiles?.reduce((currencyMappings: WalletProfileMapping, profile) => {
      const currencyCodes = profile?.currencyCodes
      const isCryptoProfile = !profile?.paymentMethods?.length

      currencyCodes?.forEach(currency => {
        if (!currencyMappings?.[currency]) {
          currencyMappings[currency] = {
            isCryptoProfile: isCryptoProfile,
            profiles: [],
          }
        }

        currencyMappings[currency].profiles.push({
          reference: profile?.reference,
          paymentMethods: profile?.paymentMethods,
        })
      })

      return currencyMappings
    }, {})
  }, [walletProfiles])

  const walletProfileOptions: ComboboxItem[] = useMemo(() => {
    if (!profileMappings || !currency) return []

    const currencyProfileMapping = profileMappings?.[currency]

    return currencyProfileMapping?.profiles
      ?.map(profile => {
        if (currencyProfileMapping?.isCryptoProfile) {
          return {
            leftText: '',
            value: profile?.reference,
          }
        }

        const currencyPaymentMethods = profile?.paymentMethods?.filter(paymentMethod =>
          CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS?.[currency]?.includes(
            paymentMethod?.toUpperCase()
          )
        )

        const options = getOptions(currencyPaymentMethods)?.sort((a, b) => a.localeCompare(b))

        const label = stringArrayToFriendlyText(options)

        return {
          leftText: label,
          value: profile?.reference,
        }
      })
      ?.sort((a, b) => a.leftText.localeCompare(b.leftText))
  }, [profileMappings, currency])

  const availableCurrencies = useMemo(() => {
    if (!enableAssignCustomerWallet) {
      const currencies = isEmbeddedCustomer
        ? [BeneficiaryDetailsCurrency.USD]
        : [BeneficiaryDetailsCurrency.EUR, BeneficiaryDetailsCurrency.GBP]

      return currencies?.map(currency => ({
        LeftElement: <Avatar name={currency?.toLowerCase()} size="small" />,
        leftText: currency,
        value: currency,
      }))
    }

    if ((enableAssignCustomerWallet && !profileMappings) || isFetchingWalletProfiles) return []

    const currencies = Object.keys(profileMappings || {})
    const cryptoCurrencies = isEmbeddedCustomer
      ? currencies?.filter(currency => profileMappings?.[currency]?.isCryptoProfile)
      : []
    const fiatCurrencies = currencies?.filter(
      currency => !profileMappings?.[currency]?.isCryptoProfile
    )

    const allCurrencies = [...cryptoCurrencies, ...fiatCurrencies]?.sort((a, b) =>
      a?.localeCompare(b)
    )

    return allCurrencies?.map(currency => ({
      LeftElement: <Avatar name={currency?.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [profileMappings, isEmbeddedCustomer, isFetchingWalletProfiles, enableAssignCustomerWallet])

  return {
    availableCurrencies,
    walletProfileOptions,
    isFetchingWalletProfiles,
    walletProfilesError,
    profileMappings,
  }
}
